import { inject, Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ClassService, AcceptInviteResult } from './class.service';

const INVITE_TOKEN_KEY = 'classjax_invite_token';

@Injectable({
  providedIn: 'root'
})
export class InviteService {
  private classService = inject(ClassService);
  private router = inject(Router);

  /**
   * Check if there's a pending invite token in localStorage
   */
  hasPendingInvite(): boolean {
    return !!localStorage.getItem(INVITE_TOKEN_KEY);
  }

  /**
   * Get the pending invite token from localStorage
   */
  getPendingInviteToken(): string | null {
    return localStorage.getItem(INVITE_TOKEN_KEY);
  }

  /**
   * Store an invite token for later acceptance
   */
  storePendingInvite(token: string): void {
    localStorage.setItem(INVITE_TOKEN_KEY, token);
  }

  /**
   * Clear the pending invite token
   */
  clearPendingInvite(): void {
    localStorage.removeItem(INVITE_TOKEN_KEY);
  }

  /**
   * Try to accept a pending invite.
   * Should be called after user is authenticated.
   * Returns the result and clears the stored token.
   */
  async acceptPendingInvite(): Promise<AcceptInviteResult | null> {
    const token = this.getPendingInviteToken();
    if (!token) {
      return null;
    }

    try {
      const result = await this.classService.acceptInvite(token);
      // Always clear the token after attempting acceptance
      this.clearPendingInvite();
      return result;
    } catch (err) {
      console.error('Failed to accept pending invite:', err);
      this.clearPendingInvite();
      return { success: false, error: 'Failed to accept invitation' };
    }
  }

  /**
   * Process any pending invite and redirect appropriately.
   * Call this after authentication to handle invite flow.
   * Returns true if an invite was processed and redirect happened.
   */
  async processPendingInvite(): Promise<boolean> {
    if (!this.hasPendingInvite()) {
      return false;
    }

    const result = await this.acceptPendingInvite();
    if (result?.success) {
      // Redirect to student dashboard
      this.router.navigate(['/student']);
      return true;
    }

    return false;
  }
}
